function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components as ReactSelectComponents } from 'react-select';
import { FormControl } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CancelIcon from '@material-ui/icons/Cancel';
import PropTypes from 'prop-types';
import { DefaultText } from '../content';
const NoOptionsMessage = props => /*#__PURE__*/React.createElement(DefaultText, _extends({
  color: "text-secondary MuiAutocomplete-noOptions"
}, props.innerProps), props.children);
const inputComponent = ({
  inputRef,
  ...props
}) => /*#__PURE__*/React.createElement("div", _extends({
  ref: inputRef
}, props));
const Control = props => {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: {
      TextFieldProps
    }
  } = props;
  return /*#__PURE__*/React.createElement(TextField, _extends({
    InputProps: {
      inputComponent,
      inputProps: {
        ref: innerRef,
        style: {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          height: 'auto'
        },
        children,
        ...innerProps
      }
    }
  }, TextFieldProps));
};
const Option = props => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(MenuItem, _extends({
    key: Date.now()
    // Long labels wrap inside the menu (which is as wide as the control) instead of running off screen.
    ,
    style: {
      backgroundColor: props.isDisabled && theme.palette.error.light,
      whiteSpace: 'normal',
      overflowWrap: 'anywhere'
    },
    ref: props.innerRef,
    selected: props.isFocused,
    component: "div"
  }, props.innerProps), props.children);
};
const Placeholder = props => {
  const {
    t
  } = useTranslation();
  const {
    innerProps = {}
  } = props;
  return /*#__PURE__*/React.createElement("p", _extends({
    className: "MuiAutocomplete-placeholder MuiTypography-colorTextSecondary"
  }, innerProps), `${t('BOOK_WIDGET.select')}...`);
};
const SingleValue = props => /*#__PURE__*/React.createElement("p", props.innerProps, props.children);

// Chips wrap in both modes: forcing one line when portalled hid the extra chips of a
// multi-select (staff, sessions).
const ValueContainer = props => /*#__PURE__*/React.createElement("div", {
  className: "MuiAutocomplete-values"
}, props.children);
const MultiValue = props => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Chip, {
    style: {
      backgroundColor: props.data.isDisabled && theme.palette.error.light
    },
    tabIndex: -1,
    label: props.children,
    onDelete: props.removeProps.onClick,
    size: "small",
    deleteIcon: /*#__PURE__*/React.createElement(CancelIcon, _extends({}, props.removeProps, {
      size: "small"
    }))
  });
};

// Keep react-select's own menu styles (position: absolute under the control) and its
// innerRef, which it uses to measure the available space. Without them the Paper rendered
// in the normal flow, so opening the list pushed everything below it down.
// When the list is portalled (dropdownPortal) the portal already positions it, and an absolute
// menu inside its overflow-hidden box would be clipped, so keep it in flow there.
const Menu = props => /*#__PURE__*/React.createElement(Paper, _extends({
  square: true,
  ref: props.innerRef
}, props.innerProps, {
  style: props.selectProps.menuPortalTarget ? {
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    boxShadow: 'none'
  } : props.getStyles('menu', props)
}), props.children);

// Chrome guesses some generic text inputs (react-select's underlying search input has
// no explicit autocomplete purpose of its own) might be a payment field, and on a
// non-HTTPS origin shows an "Automatic payment methods filling is disabled..." bubble
// that visually overlaps the option list, blocking clicks on it entirely (found in
// Coaching's athlete onboarding "pick a customer" picker, 2026-07-05, local dev over
// http). autoComplete="off" doesn't guarantee Chrome respects it for this specific
// heuristic, but it's the standard mitigation and safe to apply everywhere this shared
// component is used.
const Input = props => /*#__PURE__*/React.createElement(ReactSelectComponents.Input, _extends({}, props, {
  autoComplete: "off"
}));
const components = {
  Control,
  Input,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};
const normalizeInitialValue = (value, options, isMultiSelect) => {
  const normalizeOption = option => ({
    value: option?.value ?? option?.id,
    label: option?.label ?? option?.name
  });
  if (value === undefined || value === null || value === '') return isMultiSelect ? [] : null;
  if (Array.isArray(value)) return value.map(normalizeOption);
  if (typeof value === 'object') return normalizeOption(value);
  if (isMultiSelect && typeof value === 'string') {
    const values = value.split(',').filter(Boolean);
    return values.map(item => normalizeOption(options?.find(option => String(option.value ?? option.id) === item) || {
      value: item,
      label: item
    }));
  }
  return normalizeOption(options?.find(option => String(option.value ?? option.id) === String(value)) || {
    value,
    label: value
  });
};
const Autocomplete = ({
  id,
  name,
  label,
  onChange,
  placeholder,
  isMultiSelect,
  options,
  value,
  throwError = false,
  margin = 'normal',
  className,
  fullWidth = false,
  required = true,
  dropdownPortal = false,
  error,
  helperText,
  ...rest
}) => {
  const {
    t
  } = useTranslation();
  const [localValue, setLocalValue] = useState(() => normalizeInitialValue(value, options, isMultiSelect));
  const [errorMessage, setErrorMessage] = useState(null);
  const elementReference = useRef();
  useEffect(() => {
    if (!elementReference) {
      return;
    }
    const validationListener = event => {
      event.stopPropagation();
      setErrorMessage({
        message: event.target.errormsg,
        param: event.target.errormsgparam
      });
      setLocalValue(event.target.value);
    };
    const elements = id ? document.querySelectorAll(`.${id} [name="${elementReference.current.props.name}"]`) : document.getElementsByName(elementReference.current.props.name);
    if (!elements || !elements.length) {
      return;
    }
    if (elements.length > 1 && !isMultiSelect) {
      throw new Error('Autocomplete duplicate components error');
    }
    const element = elements[0];
    required && element.setAttribute('requiredstr', 'true');
    element.addEventListener('invalid-data', validationListener);
    return () => {
      element && element.removeEventListener('invalid-data', validationListener);
    };
  }, [elementReference, id, isMultiSelect, label, required]);
  const triggerChangeForHiddenField = elementName => {
    const element = id ? document.querySelector(`.${id} [name="${elementName}"]`) : document.querySelector(`[name="${elementName}"]`);
    const ev = new Event('input', {
      bubbles: true
    });
    ev.simulated = true;
    element.type = 'text';
    const tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(element.value + +new Date());
    }
    element.dispatchEvent(ev);
    element.type = 'hidden';
  };
  const handleChange = (value, actionMeta) => {
    setLocalValue(value);
    setErrorMessage(null);
    onChange && onChange(value, actionMeta);
    triggerChangeForHiddenField(actionMeta.name);
  };
  rest.inputId = `${+new Date()}_select`;

  // The portal is only the anchor box under the control (no height of its own): the menu inside
  // it is absolutely positioned (see Menu above), so a height cap or `overflow: hidden` here
  // clipped the whole list away — the portaled lists (calendar filter "Cliente", customer
  // forms) opened empty. The cap and the shadow go on the menu itself.
  const customStyles = {
    menuPortal: base => ({
      ...base,
      zIndex: 99999
    }),
    menu: base => ({
      ...base,
      boxShadow: '0 0 6px #ccc'
    }),
    // The list scrolls itself (so its scrollbar reaches the last options); the width stays the
    // control's own.
    menuList: base => ({
      ...base,
      maxHeight: 200,
      overflowX: 'hidden'
    })
  };
  const selectComponentOptions = {
    ...rest,
    dropdownPortal,
    ref: elementReference,
    className: `MuiAutocomplete-root ${throwError && !localValue && 'error'} ${className} ${id}`,
    inputId: 'react-select-single',
    TextFieldProps: {
      label: label,
      variant: 'outlined',
      InputLabelProps: {
        htmlFor: rest.inputId,
        shrink: true
      }
    },
    name,
    placeholder,
    options: options?.map(x => ({
      value: x.value || x.id,
      label: x.label || x.name
    })),
    components,
    value: localValue,
    onChange: handleChange,
    isMulti: isMultiSelect,
    styles: dropdownPortal && customStyles,
    menuPortalTarget: dropdownPortal && document.body,
    // A portalled list doesn't follow its field when something scrolls, so close it, unless
    // the scroll is inside the list itself.
    closeMenuOnScroll: dropdownPortal ? event => !(event.target instanceof Element && event.target.closest('.MuiPaper-root')) : undefined
  };
  if (options && isMultiSelect && typeof selectComponentOptions.value === 'string') {
    const initialValues = selectComponentOptions.value.split(',')?.filter(x => x) || [];
    selectComponentOptions.value = options.filter(x => initialValues.includes(x.value));
  }
  if (errorMessage) {
    selectComponentOptions.TextFieldProps.helperText = t(errorMessage.message).replace('[nome]', label).replace('[errormsgparam]', errorMessage.param);
    selectComponentOptions.TextFieldProps.error = true;
  }
  if (error) {
    selectComponentOptions.TextFieldProps.error = true;
    selectComponentOptions.TextFieldProps.helperText = helperText;
  }
  return /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    margin: margin,
    className: className,
    fullWidth: fullWidth
  }, /*#__PURE__*/React.createElement(Select, selectComponentOptions));
};
export default Autocomplete;
Autocomplete.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.any,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  options: PropTypes.array
};